/**
 * Transform raw DLS data to the final structure expected by the application.
 * This replicates the server-side transformation logic from transform_to_final_json.
 */

// Enum values for unit attributes (from Python AttributesUnits enum)
const ATTRIBUTES_UNITS = new Set([
  'year_built',
  'class',
  'actual_use',
  'condition',
  'frame_type',
  'view',
  'enclosed_extent',
  'covered_extent',
  'uncovered_extent',
  'basement_extent',
  'ancillary_extent',
  'ground_floor_extent',
  'mezzanine_extent',
  'parking_place_number',
  'parking_place_type',
  'flat_no',
  'floor_number',
  'disability',
  'floor_total_number',
  'refurbish_type',
  'refurbish_year',
  'bedroom_number'
]);

/**
 * Transform ParcelInfo data to the parcel_info structure
 */
function transformParcelInfo(parcelInfoData) {
  // Helper to convert 0 to null for price values
  const priceOrNull = (value) => (value === 0 || value === null || value === undefined ? null : value);

  const sheet = parcelInfoData.PrSheetValue;
  const plan = parcelInfoData.PrPlanValue;

  const parcelInfo = {
    block: parcelInfoData.PrRegistrationBlock,
    loc_id: parcelInfoData.PrQuarterId,
    location: parcelInfoData.PrLocation,
    parcel_area: parcelInfoData.PrParcelExtent,
    parcel_id: parcelInfoData.PrParcelId,
    parcel_number: parcelInfoData.PrParcelNo,
    plan: plan,
    price1: priceOrNull(parcelInfoData.PrPriceBase1),
    price2: priceOrNull(parcelInfoData.PrPriceBase2),
    price3: priceOrNull(parcelInfoData.PrPriceBase3),
    property_id: parcelInfoData.PrPropertyId,
    regist_no: parcelInfoData.PrRegistrationNo,
    scale: parcelInfoData.PrScaleValue,
    sheet: sheet,
    // Combined sheet/plan field for display
    sheet_plan: sheet && plan ? `${sheet}/${plan}` : sheet || plan || null,
    type: parcelInfoData.PrSubPropertyKindNameEl
  };

  return parcelInfo;
}

/**
 * Fetch building name from fiscal data
 */
function fetchBuildingName(buildingId, fiscal) {
  if (!fiscal.buildingFiscalData) return null;

  for (const building of fiscal.buildingFiscalData) {
    if (building.BuildingId === buildingId && building.DbFieldNameCodeEnDescr === 'Building Name') {
      return building.FisValue ? building.FisValue.trim() : null;
    }
  }
  return null;
}

/**
 * Transform unit features from fiscal info
 */
function transformUnitFeatures(fiscalInfo, filteredParcelInfo) {
  const units = [];

  if (!fiscalInfo.parcelUnits) return units;

  for (const unit of fiscalInfo.parcelUnits) {
    if (unit.PrPropertyId === filteredParcelInfo.PrPropertyId) {
      const unitFeatures = {
        parcel_id: unit.PrParcelId,
        property_id: unit.PrPropertyId,
        unit_id: unit.PrUnitId,
        type: unit.PrSubPropertyKind?.NameGr || null,
        create_date: unit.CreateDate || null,
        update_date: unit.UpdateDate || null,
        building_name: fetchBuildingName(unit.PrBuildingId, fiscalInfo)
      };

      const extraDict = {};

      if (fiscalInfo.unitFiscalData) {
        for (const x of fiscalInfo.unitFiscalData) {
          if (String(x.UnitId) === String(unit.PrUnitId)) {
            const feature = x.DbFieldNameCodeEnDescr.replace('Unit ', '').replace(/ /g, '_').toLowerCase();

            if (ATTRIBUTES_UNITS.has(feature)) {
              unitFeatures[feature] = x.ActValueGrDescr && x.ActValueGrDescr.trim() ? x.ActValueGrDescr.trim() : null;
            } else {
              if (x.ActValueGrDescr) {
                extraDict[feature] = x.ActValueGrDescr;
              }
            }
          }
        }
      }

      if (Object.keys(extraDict).length > 0) {
        unitFeatures.extra_info = extraDict;
      }

      units.push(unitFeatures);
    }
  }

  return units;
}

/**
 * Transform zone info from parcel plan zones
 */
function transformZoneInfo(parcelInfo) {
  const zoneInfo = [];

  if (!parcelInfo.ParcelPlanZones) return zoneInfo;

  for (const planZone of parcelInfo.ParcelPlanZones) {
    const zone = {
      affected_extent: planZone.PrAffectedExtent,
      affected_extent_pc: planZone.PrTotalExtent ? planZone.PrAffectedExtent / planZone.PrTotalExtent : 0,
      coverage: planZone.PrPlanningZone?.PrCoverageRate || null,
      create_date: planZone.CreateDate || null,
      density: planZone.PrPlanningZone?.PrDensityRateQty || null,
      floors: planZone.PrPlanningZone?.PrStoreyNoQty || null,
      height_meters: planZone.PrPlanningZone?.PrHeightMSR || null,
      parcel_id: planZone.PrParcelId,
      plan_zone_name: planZone.PrPlanningZone?.PrName || null,
      update_date: planZone.UpdateDate || null,
      zone_descr: planZone.PrPlanningZone?.PrNameGr || null,
      zone_id: planZone.Id
    };
    zoneInfo.push(zone);
  }

  return zoneInfo;
}

/**
 * Transform geometry to string representation
 */
function transformGeometryToString(geoInfo) {
  if (!geoInfo || !geoInfo.features || geoInfo.features.length === 0) {
    return null;
  }

  const firstFeature = geoInfo.features[0];
  return JSON.stringify({
    type: firstFeature.geometry.type,
    coordinates: firstFeature.geometry.coordinates
  });
}

/**
 * Main transformation function - converts raw DLS data to the final JSON structure
 * @param {Object} rawData - Raw data from /api/dls/external_property_raw endpoint
 * @param {number|string} registrationBlock - The registration block number
 * @param {string} registrationNo - The registration number
 * @returns {Object} Transformed data in the expected format
 */
export function transformDlsData(rawData, registrationBlock, registrationNo) {
  const { parcel_info: parcelInfoList, parcel_fiscal: parcelFiscal, geo_info: geoInfo } = rawData;

  // Filter the parcel_info list to find the item matching the registration_block and registration_no
  const filteredParcelInfo = parcelInfoList.find(
    (item) => item.PrRegistrationBlock === parseInt(registrationBlock) && item.PrRegistrationNo === String(registrationNo)
  );

  if (!filteredParcelInfo) {
    throw new Error('No matching parcel_info found');
  }

  // Initialize the final structure
  const zoneInfo = transformZoneInfo(filteredParcelInfo);

  // Transform all parcel registrations for the Parcel Registrations tab
  const parcelRegistrations = parcelInfoList.map((item) => transformParcelInfo(item));

  const finalStructure = {
    // NOTE: `Id` here is DLS's PrPropertyId, not a parcel identifier — it happened to be read
    // as "parcel_id" by an earlier version of this transform, which silently broke anything
    // keyed off it (this KML export, and the Transactions tab). PrParcelId is what DLS's own
    // ArcGIS cadastral layer calls SBPI_ID_NO, and what every other "parcel_id" in this app
    // (parcel_info.parcel_id below, the server-side transform_to_final_json, our own DB) means.
    identifiers: { parcel_id: parseInt(filteredParcelInfo.PrParcelId) },
    location: {
      district: filteredParcelInfo.PrDistrict?.NameGr || null,
      municipality: filteredParcelInfo.PrMunicipality?.NameGr || null,
      quarter: filteredParcelInfo.PrQuarter?.NameGr || null,
      block: filteredParcelInfo.PrRegistrationBlock,
      regist_no: filteredParcelInfo.PrRegistrationNo
    },
    parcel_characteristics: {},
    parcel_info: transformParcelInfo(filteredParcelInfo),
    parcel_registrations: parcelRegistrations,
    unit_features: transformUnitFeatures(parcelFiscal, filteredParcelInfo),
    zone_info: zoneInfo
  };

  // Processing parcel_characteristics from parcelFiscalData
  if (parcelFiscal.parcelFiscalData) {
    for (const item of parcelFiscal.parcelFiscalData) {
      const key = item.DbFieldNameCodeEnDescr.replace(/ /g, '_').toLowerCase();
      finalStructure.parcel_characteristics[key] = item.ActValueGrDescr;
    }
  }

  // Add geometry
  finalStructure.parcel_characteristics.polygon_geom = transformGeometryToString(geoInfo);

  // Add planning zones
  finalStructure.parcel_characteristics.planning_zone = zoneInfo.map((zone) => zone.plan_zone_name);

  return finalStructure;
}

export default transformDlsData;
